import React, { useEffect, useState, useMemo } from "react";
import { CloudRain, Sun, Wind, Snowflake, Timer, Lightning, Fire } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { TeamLogo } from "@/components/TeamVisuals";
import { teamTextColor } from "@/lib/teams";
import { useTheme } from "@/components/ThemeToggle";

// Total O/U → glow accent color
function totalGlow(ou) {
  if (ou >= 51) return "#39FF14";       // hot game — green glow
  if (ou >= 46) return "#FFCC00";       // warm — yellow glow
  if (ou >= 42) return "#FF8A00";       // mid — orange glow
  return "#FF3B30";                     // dud — red glow
};
// Sprint 63.1 — hand-tuned light-mode palette (same thresholds, ink-safe hues on off-white).
const totalGlowLight = (ou) => {
  if (ou >= 51) return "#1a8f08";
  if (ou >= 46) return "#a37a00";
  if (ou >= 42) return "#c2410c";
  return "#c8261c";
}

function weatherIcon(w) {
  if (!w) return null;
  const cond = (w.condition || "").toLowerCase();
  if (cond.includes("rain") || cond.includes("shower")) return <CloudRain size={11} weight="fill" />;
  if (cond.includes("snow")) return <Snowflake size={11} weight="fill" />;
  if ((w.wind_mph || 0) >= 15) return <Wind size={11} weight="fill" />;
  if (w.indoor || w.dome) return <span className="text-[9px] mono">DOME</span>;
  return <Sun size={11} weight="fill" />;
}

function fmtKickoff(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const day = d.toLocaleDateString("en-US", { weekday: "short", timeZone: "America/New_York" }).toUpperCase();
    const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" });
    return `${day} ${time} ET`;
  } catch { return ""; }
}

// Sprint 60.60 — human-friendly age formatter for the "Cached · Xm" pill
// that surfaces when Vegas is served from Mongo fallback (SGO outage).
function fmtAge(sec) {
  if (sec == null || sec < 0) return "";
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

export default function GameTicker() {
  const [vegas, setVegas] = useState([]);
  const [weather, setWeather] = useState({});
  const [pivots, setPivots] = useState({});
  // Sprint 60.52 — loading state + Vegas coverage tracking so the
  // ticker never renders stale data during a slate switch, and
  // surfaces a "Vegas partial" pill when SGO didn't cover every
  // game in the applied slate (helps users understand why some
  // rows show `—` instead of thinking the app is broken).
  const [loading, setLoading] = useState(false);
  const isLight = useTheme() === "light";
  const [coverage, setCoverage] = useState({ vegas_covered: null, total: null });
  // Sprint 60.60 — track Vegas freshness so we can show a "stale · from
  // cache" pill when serving Mongo-warmed data during an SGO outage.
  const [vegasSource, setVegasSource] = useState("live"); // "live" | "cache" | "none"
  const [vegasAgeSec, setVegasAgeSec] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const s = await api.slateGames();
        setVegas(s?.games || []);
        setCoverage({
          vegas_covered: s?.vegas_covered ?? null,
          total: s?.total ?? null,
        });
        setVegasSource(s?.vegas_source || "live");
        setVegasAgeSec(s?.vegas_age_seconds ?? null);
      } catch (e) { console.debug("game ticker load failed:", e?.message); }
      try {
        const snap = await api.dataSnapshot();
        setWeather(snap?.weather_by_game || {});
      } catch (e) { /* silent */ }
      try {
        const p = await api.slateGamePivots();
        setPivots(p?.games || {});
      } catch (e) { /* pool not uploaded yet */ }
    };
    load();
    const onPool = () => load();
    // Sprint 60.52 — during a slate apply the backend may take 2-6s to
    // rebuild derived state. Show skeleton state so the user doesn't
    // see stale rows and think we're broken (the exact "freeze"
    // pattern reported on production).
    const onLoading = (ev) => {
      const isLoading = !!(ev?.detail?.loading);
      setLoading(isLoading);
      if (!isLoading) load();
    };
    window.addEventListener("wp:pool-refreshed", onPool);
    window.addEventListener("wp:slate-loading", onLoading);
    const t = setInterval(load, 60_000);
    return () => {
      clearInterval(t);
      window.removeEventListener("wp:pool-refreshed", onPool);
      window.removeEventListener("wp:slate-loading", onLoading);
    };
  }, []);

  const games = useMemo(
    () => [...vegas]
      .sort((a, b) => (b.total || b.over_under || 0) - (a.total || a.over_under || 0)),
    [vegas],
  );

  // Show 3-tile skeleton during active slate load, even before we
  // have any games — better than a full component collapse.
  if (loading && games.length === 0) {
    return (
      <div data-testid="game-ticker-loading" className="w-full bg-[#0a0a0a] border-b border-zinc-900">
        <div className="flex items-center gap-3 px-4 py-2.5">
          <div className="mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase font-bold flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-[#FFCC00] rounded-full animate-pulse" />
            Loading slate…
          </div>
          <div className="flex-1 flex gap-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-9 flex-1 max-w-[220px] bg-zinc-900/50 border border-zinc-800 animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }
  if (games.length === 0) return null;

  const cov = coverage.vegas_covered;
  const tot = coverage.total;
  const hasCoverageGap = cov !== null && tot !== null && cov < tot;

  return (
    <div data-testid="game-ticker" className="game-ticker-bar w-full bg-gradient-to-b from-[#0a0a0a] via-[#0d0d0d] to-[#080808] border-b border-zinc-900 relative overflow-hidden">
      {/* Ambient noise/grain via radial gradients */}
      <div className="absolute inset-0 pointer-events-none opacity-40"
           style={{ background: "radial-gradient(1200px 60px at 50% 0%, rgba(57,255,20,0.08), transparent 70%)" }} />
      <div className="relative flex items-start gap-3 px-4 py-2.5">
        <div className="flex items-center gap-2 flex-shrink-0 border-r border-zinc-800 pr-3 pt-1.5">
          <div className={`w-1.5 h-1.5 rounded-full ${loading ? "bg-[#FFCC00] animate-pulse" : "bg-[#39FF14] animate-pulse"}`} />
          <div className="mono text-[9px] tracking-[0.25em] text-zinc-400 uppercase font-bold">
            {loading ? "Loading…" : "Live · Slate"}
          </div>
          {hasCoverageGap && !loading && (
            <span
              data-testid="game-ticker-coverage-pill"
              title={`SGO covered ${cov} of ${tot} games — the remaining ${tot - cov} game(s) show \`—\` until Vegas posts their line.`}
              className="mono text-[9px] uppercase tracking-widest border border-[#FF3B30] text-[#FF3B30] px-1.5 py-0.5"
            >
              Vegas {cov}/{tot}
            </span>
          )}
          {vegasSource === "cache" && !loading && (
            <span
              data-testid="game-ticker-stale-pill"
              title={`Live Vegas feed is temporarily unavailable. Showing last-known-good numbers from cache${
                vegasAgeSec != null ? ` (${fmtAge(vegasAgeSec)} old)` : ""
              }. The ticker auto-refreshes every 60s.`}
              className="mono text-[9px] uppercase tracking-widest border border-[#FFCC00] text-[#FFCC00] px-1.5 py-0.5"
            >
              Cached{vegasAgeSec != null ? ` · ${fmtAge(vegasAgeSec)}` : ""}
            </span>
          )}
        </div>
        <div className={`flex-1 transition-opacity duration-200 ${loading ? "opacity-40" : "opacity-100"}`}>
          <div className="flex flex-wrap gap-2.5">
            {games.map((g, idx) => {
              const ou = Number(g.total || g.over_under || 0);
              const ouKnown = ou > 0;
              // When Vegas hasn't overlaid yet, use a neutral zinc glow
              // instead of the "dud/red" 0.0 color — the ticker still
              // renders the matchup so users see their slate at a glance.
              const glow = ouKnown ? (isLight ? totalGlowLight(ou) : totalGlow(ou)) : (isLight ? "#6b6b74" : "#a1a1aa");
              const gid = g.game_id || `${g.away}@${g.home}`;
              const w = weather[gid] || null;
              const piv = pivots[gid] || null;
              return (
                <div
                  key={gid || idx}
                  className="relative group flex-shrink-0"
                  style={{ perspective: "600px" }}
                >
                  <div
                    className="game-ticker-card relative bg-gradient-to-br from-[#151515] to-[#0a0a0a] border border-zinc-800 hover:border-zinc-600 transition-all duration-200 min-w-[260px] group-hover:-translate-y-0.5 group-hover:scale-[1.02]"
                    style={{
                      boxShadow: isLight
                        ? `0 2px 8px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04), 0 0 16px ${glow}22`
                        : `0 4px 12px rgba(0,0,0,0.6), 0 0 0 1px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04), 0 0 20px ${glow}22`,
                      transformStyle: "preserve-3d",
                    }}
                  >
                    <div className="px-3 py-2 flex items-center gap-3">
                    {/* Away team */}
                    <div className="flex flex-col items-center gap-0.5 min-w-[38px]">
                      <TeamLogo team={g.away} size={26} />
                      <div className="mono text-[11px] font-black" style={{ color: teamTextColor(g.away) }}>{g.away}</div>
                      <div className="game-ticker-itt mono text-[10px] font-bold text-white bg-black/50 px-1 border border-zinc-800"
                           title="Implied team total">
                        {g.away_itt != null && Number(g.away_itt) > 0
                          ? Number(g.away_itt).toFixed(1)
                          : "—"}
                      </div>
                    </div>

                    {/* @ separator + O/U pill (game total) */}
                    <div className="flex flex-col items-center gap-0.5">
                      <div className="mono text-[9px] text-zinc-600">O/U</div>
                      <div
                        className="mono text-[10px] font-black tracking-widest px-1.5 py-0.5 border"
                        style={{
                          color: glow,
                          borderColor: `${glow}66`,
                          background: `${glow}0f`,
                          textShadow: ouKnown && !isLight ? `0 0 8px ${glow}80` : "none",
                        }}
                        title={ouKnown
                          ? `Vegas Over/Under — combined points expected: ${ou}`
                          : "Vegas O/U not available for this game yet — waiting on sportsbook overlay"}
                      >
                        {ouKnown ? ou.toFixed(1) : "—"}
                      </div>
                    </div>

                    {/* Home team */}
                    <div className="flex flex-col items-center gap-0.5 min-w-[38px]">
                      <TeamLogo team={g.home} size={26} />
                      <div className="mono text-[11px] font-black" style={{ color: teamTextColor(g.home) }}>{g.home}</div>
                      <div className="game-ticker-itt mono text-[10px] font-bold text-white bg-black/50 px-1 border border-zinc-800"
                           title="Implied team total">
                        {g.home_itt != null && Number(g.home_itt) > 0
                          ? Number(g.home_itt).toFixed(1)
                          : "—"}
                      </div>
                    </div>

                    {/* Meta column: kickoff + weather */}
                    <div className="flex flex-col items-end gap-0.5 flex-1 border-l border-zinc-800 pl-2 ml-1">
                      <div className="flex items-center gap-1 text-zinc-500">
                        <Timer size={9} weight="fill" />
                        <span className="mono text-[8px] tracking-wider uppercase">{fmtKickoff(g.kickoff)}</span>
                      </div>
                      {w && (
                        <div className="flex items-center gap-1 text-zinc-400"
                             title={`${w.condition || ""} · ${w.temp_f || "?"}°F · Wind ${w.wind_mph || 0} mph`}>
                          <span style={{ color: (w.wind_mph || 0) >= 15 ? (isLight ? "#c2410c" : "#FF8A00") : (isLight ? "#6b6b74" : "#a1a1aa") }}>
                            {weatherIcon(w)}
                          </span>
                          <span className="mono text-[9px]">
                            {w.temp_f != null ? `${Math.round(w.temp_f)}°` : "—"}
                            {(w.wind_mph || 0) >= 8 && ` · ${Math.round(w.wind_mph)}mph`}
                          </span>
                        </div>
                      )}
                    </div>
                    </div>
                    {/* Leverage Pivot row — WarPaint native ownership model */}
                    {piv && (piv.chalk?.name || piv.pivot?.name) && (
                      <div
                        data-testid={`game-pivot-${g.game_id}`}
                        className="border-t border-zinc-800/70 px-3 py-1.5 grid grid-cols-2 gap-2 bg-black/30"
                      >
                        <div className="flex items-center gap-1.5 min-w-0" title={`Highest projected ownership · ${piv.chalk?.name} · ${piv.chalk?.proj?.toFixed(1)} proj`}>
                          <Fire size={10} weight="fill" className="text-[#FF3B30] flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="mono text-[8px] tracking-widest text-[#FF3B30] uppercase font-bold leading-tight">Chalk</div>
                            <div className="flex items-baseline gap-1 min-w-0">
                              <span className="text-[10px] font-bold text-zinc-100 truncate">{piv.chalk?.name}</span>
                              <span className="mono text-[9px] text-[#FF3B30] flex-shrink-0">{piv.chalk?.own?.toFixed(0)}%</span>
                              <span className="mono text-[9px] text-zinc-500 flex-shrink-0">{piv.chalk?.proj?.toFixed(1)}</span>
                            </div>
                          </div>
                        </div>
                        {piv.pivot?.name ? (
                          <div className="flex items-center gap-1.5 min-w-0 border-l border-zinc-800 pl-2" title={`Best leverage pivot · ${piv.pivot.name} · ${piv.pivot.proj?.toFixed(1)} proj vs ${piv.chalk?.proj?.toFixed(1)} chalk`}>
                            <Lightning size={10} weight="fill" className="text-[#39FF14] flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <div className="mono text-[8px] tracking-widest text-[#39FF14] uppercase font-bold leading-tight">Pivot</div>
                              <div className="flex items-baseline gap-1 min-w-0">
                                <span className="text-[10px] font-bold text-zinc-100 truncate">{piv.pivot.name}</span>
                                <span className="mono text-[9px] text-[#39FF14] flex-shrink-0">{piv.pivot.own?.toFixed(0)}%</span>
                                <span className="mono text-[9px] text-zinc-500 flex-shrink-0">{piv.pivot.proj?.toFixed(1)}</span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 min-w-0 border-l border-zinc-800 pl-2"
                               data-testid={`game-no-pivot-${g.game_id}`}
                               title={`No same-position pivot in this game projects within range of ${piv.chalk?.name} (floor ${piv.pivot_proj_floor ?? "—"} pts) — fading the chalk here costs real points`}>
                            <Lightning size={10} weight="fill" className="text-zinc-700 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <div className="mono text-[8px] tracking-widest text-zinc-600 uppercase font-bold leading-tight">Pivot</div>
                              <div className="mono text-[9px] text-zinc-600 truncate">none within range</div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Reflection / floor bounce for the 3D feel */}
                  <div
                    className="absolute left-2 right-2 -bottom-1 h-1 opacity-40 blur-sm"
                    style={{
                      background: `linear-gradient(to bottom, ${glow}55, transparent)`,
                      transform: "scaleY(-1)",
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
